import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileText, Info, Loader2, Plus, Tag } from 'lucide-react';
import {
  binder,
  siteUsers,
  regulatoryTags,
  type BinderStatus,
  type DocumentVersionRow,
  type ApprovalRoutingApprover,
} from '@/api/client';
import { fmtDateTime } from '@/lib/dates.js';
import { humanStatus } from '@/lib/humanise';
import { NewDocumentSlotDialog } from './NewDocumentSlotDialog';
import { BlindedNotice } from '@/components/layout/BlindedNotice';
import { useEisfPermissions } from '@/hooks/useEisfPermissions';
import { useDocusealSelfHeal } from '@/hooks/useDocusealSelfHeal';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * Silently self-heals a single in_approval version against DocuSeal —
 * catches a signature completed via the EMAILED link (no in-app dialog
 * open) as soon as this folder is viewed, not only when someone opens the
 * specific document. Renders nothing; one instance per in_approval doc in
 * the folder.
 */
function SilentApprovalSelfHeal({ versionId }: { versionId: string }) {
  useDocusealSelfHeal(versionId, true);
  return null;
}

const NO_UPLOAD_PERMISSION_MESSAGE = "You don't have permission to upload documents in this binder";

export interface DocumentSlotListProps {
  binderId: string;
  folderId: string;
  folderLabel: string;
  folderLevel?: string;
  onOpenViewer?: (docId: string, versionId?: string) => void;
}

type Tab = 'all' | 'approval_needed' | 'approved';

// ── Inline approval routing panel ──────────────────────────────────────────
function ApprovalRoutingPanel({
  binderId,
  docId,
  versionId,
}: {
  binderId: string;
  docId: string;
  versionId: string;
}) {
  const routingQ = useQuery({
    queryKey: ['binder-approval-routing', binderId, docId, versionId],
    queryFn: () => binder.getApprovalRouting(binderId, docId, versionId),
    staleTime: 30_000,
  });

  const approvers = routingQ.data?.approvers ?? [];
  const signedCount = approvers.filter((a) => a.status === 'signed' || a.status === 'skipped').length;
  const totalCount = approvers.length;
  const allSigned = totalCount > 0 && signedCount === totalCount;
  const anyRejected = approvers.some((a) => a.status === 'rejected');

  return (
    <tr>
      <td colSpan={5} className="px-6 pb-3 pt-0">
        {routingQ.isLoading ? (
          <div className="flex items-center gap-2 text-xs text-ink-muted">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading approvers…
          </div>
        ) : approvers.length === 0 ? (
          <p className="text-[11px] italic text-ink-muted">
            No approval routing rows yet. Use "Send via DocuSeal" to initiate the signing workflow and create approver rows.
          </p>
        ) : (
          <div className="overflow-hidden rounded border border-surface-border bg-surface-muted/40">
            {/* Progress summary bar */}
            <div className="flex items-center justify-between border-b border-surface-border px-3 py-1.5">
              <span className="text-[11px] font-medium text-ink-muted">
                {anyRejected
                  ? 'Rejected — document must be re-opened for revision'
                  : allSigned
                    ? 'All approvers have signed — ready to promote to Current'
                    : `${signedCount} of ${totalCount} approved`}
              </span>
              {/* Mini progress bar */}
              {!anyRejected && (
                <div className="flex h-1.5 w-24 overflow-hidden rounded-full bg-surface-border">
                  <div
                    className={`h-full rounded-full transition-all ${allSigned ? 'bg-emerald-500' : 'bg-accent-500'}`}
                    style={{ width: totalCount > 0 ? `${(signedCount / totalCount) * 100}%` : '0%' }}
                  />
                </div>
              )}
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-surface-border text-[10px] uppercase tracking-wide text-ink-muted">
                  <th className="px-3 py-1.5 text-left font-medium">Approver</th>
                  <th className="px-3 py-1.5 text-left font-medium">Role</th>
                  <th className="px-3 py-1.5 text-left font-medium">Meaning</th>
                  <th className="px-3 py-1.5 text-left font-medium">Status</th>
                  <th className="px-3 py-1.5 text-left font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {approvers.map((a) => (
                  <tr
                    key={a.routing_id}
                    className="border-b border-surface-border/60 last:border-0"
                  >
                    <td className="px-3 py-1.5 font-medium text-ink-primary">{a.user_name}</td>
                    <td className="px-3 py-1.5 text-ink-muted">{a.role}</td>
                    <td className="px-3 py-1.5 text-ink-muted">{a.signature_meaning}</td>
                    <td className="px-3 py-1.5">
                      <span className={`badge text-[10px] ${routingStatusBadge(a.status)}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 tabular-nums text-ink-muted">
                      {a.signed_at
                        ? fmtDateTime(a.signed_at)
                        : a.rejected_at
                          ? fmtDateTime(a.rejected_at)
                          : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </td>
    </tr>
  );
}

// ── Expanded version-history rows ───────────────────────────────────────────
// The main row only shows the CURRENT/latest version — older versions (e.g.
// a document uploaded 4 times shows only v4.0) were previously only reachable
// via the document viewer modal's prev/next arrows. This renders every OTHER
// version directly in the table, lazily fetched on expand.
function VersionHistoryRows({
  binderId,
  documentId,
  currentVersionId,
  siteUserNameMap,
}: {
  binderId: string;
  documentId: string;
  currentVersionId: string | null;
  siteUserNameMap: Map<string, string>;
}) {
  const versionsQ = useQuery({
    queryKey: ['binder-versions', binderId, documentId],
    queryFn: () => binder.listVersions(binderId, documentId),
    staleTime: 30_000,
  });

  if (versionsQ.isLoading) {
    return (
      <tr>
        <td colSpan={5} className="px-3 py-2 text-xs text-ink-muted">
          <Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" /> Loading version history…
        </td>
      </tr>
    );
  }

  const uploaderNames = versionsQ.data?.uploader_names ?? {};
  const olderVersions = (versionsQ.data?.versions ?? []).filter(
    (ver) => ver.version_id !== currentVersionId,
  );

  if (olderVersions.length === 0) {
    return null;
  }

  return (
    <>
      {olderVersions.map((ver) => (
        <tr key={ver.version_id} className="border-b border-surface-border bg-surface-muted/20 last:border-0">
          {/* File — older versions are history-only, not openable. Only the
              current/latest version (the row above) links to the viewer. */}
          <td className="px-3 py-2">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 shrink-0 text-ink-muted/60" />
              <div className="min-w-0">
                <div className="truncate font-medium text-ink-muted" title={ver.file_name}>{ver.file_name}</div>
              </div>
            </div>
          </td>
          {/* Status */}
          <td className="px-3 py-2 text-xs">
            <span className={`badge ${badgeForBinderStatus(ver.status)}`}>{humanStatus(ver.status)}</span>
          </td>
          {/* Version */}
          <td className="px-3 py-2 font-mono text-xs text-ink-primary">{ver.version_label}</td>
          {/* Uploaded */}
          <td className="px-3 py-2 text-xs text-ink-muted">{fmtDateTime(ver.uploaded_at)}</td>
          {/* Uploaded by */}
          <td className="px-3 py-2 text-xs text-ink-muted">
            {uploaderNames[ver.uploaded_by] || siteUserNameMap.get(ver.uploaded_by) || '—'}
          </td>
        </tr>
      ))}
    </>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export function DocumentSlotList({
  binderId,
  folderId,
  folderLabel,
  folderLevel,
  onOpenViewer,
}: DocumentSlotListProps) {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('all');
  const [showArchived, setShowArchived] = useState(false);
  const { can: canEisf } = useEisfPermissions();
  // Filing a document into a slot is its own permission (binder.upload, split
  // from binder.update on 2026-10-09). This was a `void` call kept "for future
  // use" — there is now an operation behind it, so it decides whether the
  // upload dialog offers to attach a file.
  const canUploadVersion = canEisf('upload_document_version');

  const queryKey = ['binder', binderId, 'folder', folderId, 'documents', showArchived];

  const list = useQuery({
    queryKey,
    queryFn: () => binder.listFolderDocuments(binderId, folderId, showArchived),
    staleTime: 0,
  });

  const documents = list.data?.documents ?? [];
  const archivedCount = list.data?.archived_count ?? 0;
  const currentVersions = list.data?.current_versions ?? {};
  const tagsByDoc = list.data?.tags ?? {};
  const uploaderNames = list.data?.uploader_names ?? {};
  // Phase 1 item 12 (AC14) — server-side blinding: everything below is
  // empty and the folder body renders a redaction notice instead.
  const blinded = list.data?.blinded === true;
  const blindedRoleMasterRef = list.data?.blinded_role_master_ref ?? null;

  const siteUsersQ = useQuery({
    queryKey: ['site-users'],
    queryFn: siteUsers.listMine,
    staleTime: 5 * 60_000,
  });
  const siteUserNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of siteUsersQ.data?.users ?? []) m.set(u.user_id, u.full_name);
    return m;
  }, [siteUsersQ.data]);

  // A doc needs the fallback fetch when either its version data is missing
  // (current_version_id still null server-side, brand-new slot) OR its
  // version data IS present but the uploader's name didn't resolve in the
  // primary listFolderDocuments response — e.g. a transient Caspio lookup
  // miss right after upload. Without the second condition, "Uploaded by"
  // stays blank on the freshly-uploaded row until some unrelated later
  // action (send for approval, sign) happens to trigger a refetch that
  // succeeds — this gives it another independent chance immediately.
  const missingDocIds = useMemo(
    () =>
      documents
        .filter((d) => {
          const cv = currentVersions[d.document_id];
          if (!cv) return true;
          return !uploaderNames[cv.uploaded_by] && !siteUserNameMap.get(cv.uploaded_by);
        })
        .map((d) => d.document_id),
    [documents, currentVersions, uploaderNames, siteUserNameMap],
  );
  const fallbackBatchQ = useQuery({
    queryKey: ['binder-versions-batch', binderId, folderId, missingDocIds],
    queryFn: async () => {
      const results = await Promise.all(
        missingDocIds.map((docId) => binder.listVersions(binderId, docId)),
      );
      const versions: Record<string, DocumentVersionRow | null> = {};
      const uploaderNames: Record<string, string> = {};
      missingDocIds.forEach((docId, i) => {
        versions[docId] = results[i]?.versions[0] ?? null;
        Object.assign(uploaderNames, results[i]?.uploader_names ?? {});
      });
      return { versions, uploaderNames };
    },
    enabled: !!binderId && missingDocIds.length > 0,
    staleTime: 30_000,
  });
  const resolvedVersions = useMemo(() => {
    if (missingDocIds.length === 0) return currentVersions;
    return { ...currentVersions, ...(fallbackBatchQ.data?.versions ?? {}) };
  }, [currentVersions, missingDocIds, fallbackBatchQ.data]);
  // The folder-level list only resolves uploader names for docs whose
  // current version it already knew about. A doc that just got its first
  // version (current_version_id still null server-side) falls through to
  // `fallbackBatchQ` for its version data — extend the same fallback to
  // uploader names, or "Uploaded by" stays blank until the next unrelated
  // refetch happens to resolve it (e.g. reopening the doc).
  const resolvedUploaderNames = useMemo(
    () => ({ ...uploaderNames, ...(fallbackBatchQ.data?.uploaderNames ?? {}) }),
    [uploaderNames, fallbackBatchQ.data],
  );

  const tagsQ = useQuery({
    queryKey: ['regulatory-tags'],
    queryFn: regulatoryTags.list,
    staleTime: 5 * 60_000,
  });
  const tagLabels = useMemo(() => {
    const m = new Map<string, { label: string; category: string }>();
    for (const t of tagsQ.data?.tags ?? []) {
      m.set(t.tag_id, { label: t.label, category: t.category });
    }
    return m;
  }, [tagsQ.data]);

  // Only documents that have a file uploaded
  const uploadedDocs = useMemo(
    () => documents.filter((d) => !!resolvedVersions[d.document_id]),
    [documents, resolvedVersions],
  );

  const approvalNeededDocs = useMemo(
    () => uploadedDocs.filter((d) => d.status === 'in_approval'),
    [uploadedDocs],
  );
  // Self-heal every in_approval document's current version the moment this
  // folder is viewed — not just when someone opens that specific document.
  // Catches a signature completed via the emailed link even if the user
  // never navigates past this list.
  const inApprovalVersionIds = useMemo(
    () =>
      [...new Set(
        approvalNeededDocs
          .map((d) => resolvedVersions[d.document_id]?.version_id)
          .filter((id): id is string => !!id),
      )],
    [approvalNeededDocs, resolvedVersions],
  );
  const approvedDocs = useMemo(
    () => uploadedDocs.filter((d) => d.status === 'current'),
    [uploadedDocs],
  );

  const tabDocs = useMemo(() => {
    switch (activeTab) {
      case 'approval_needed': return approvalNeededDocs;
      case 'approved':        return approvedDocs;
      default:                return uploadedDocs;
    }
  }, [activeTab, uploadedDocs, approvalNeededDocs, approvedDocs]);

  const isFallbackLoading = fallbackBatchQ.isLoading;
  // Only true before this query has ever resolved — gates the full-panel
  // spinner (no table shell to show yet).
  const isLoading = list.isLoading;
  // True whenever the visible rows might not reflect the latest server
  // state: the version-batch fallback still resolving for new slots, or a
  // background refetch (e.g. after Send for Approval / Sign / any status
  // transition invalidates this query). list.isFetching alone doesn't clear
  // list.data, so without this the table would silently keep showing
  // pre-update values with no indication a refresh is in flight.
  const isRefreshing = list.isFetching || (list.isSuccess && isFallbackLoading);

  const TABS: { id: Tab; label: string; count: number }[] = [
    { id: 'all',              label: 'All',              count: uploadedDocs.length },
    { id: 'approval_needed',  label: 'Approval Needed',  count: approvalNeededDocs.length },
    { id: 'approved',         label: 'Approved',         count: approvedDocs.length },
  ];

  // Phase 2 Defect 2.1 — hide the "New document" button when the caller's
  // role lacks create_document_slot per the eISF permission matrix.
  // Read-only roles (PI without editor rights, monitor, auditor, etc.)
  // never see the button rather than getting a 403 after submitting.
  const perms = useEisfPermissions();
  const canCreateSlot = perms.can('create_document_slot');

  return (
    <div className="space-y-0">
      {inApprovalVersionIds.map((versionId) => (
        <SilentApprovalSelfHeal key={versionId} versionId={versionId} />
      ))}
      {/* Header */}
      <div className="flex items-center justify-between gap-3 pb-3">
        <div>
          <h2 className="text-base font-semibold text-ink-primary">{folderLabel}</h2>
          <p className="text-xs text-ink-muted">
            {blinded
              ? 'Content hidden — you’re blinded on this study.'
              : uploadedDocs.length === 0
                ? 'No documents uploaded yet.'
                : `${uploadedDocs.length} document${uploadedDocs.length === 1 ? '' : 's'} · click a row to manage`}
          </p>
        </div>
        <div className="flex items-center gap-2">
        {!blinded && (archivedCount > 0 || showArchived) && (
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-ink-secondary">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show discarded{archivedCount > 0 ? ` (${archivedCount})` : ''}
          </label>
        )}
        {!blinded && (
          <button
            type="button"
            className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => setCreating(true)}
            disabled={!canCreateSlot}
            title={canCreateSlot ? undefined : NO_UPLOAD_PERMISSION_MESSAGE}
          >
            <Plus className="h-4 w-4" /> New document
          </button>
        )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-0 border-b border-surface-border">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-accent-600 text-accent-700'
                : 'border-transparent text-ink-muted hover:text-ink-primary'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span
                className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                  activeTab === tab.id
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-surface-muted text-ink-muted'
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Document table */}
      <div className="card overflow-hidden rounded-tl-none">
        {isLoading && (
          <div className="flex items-center justify-center gap-2 p-8 text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading documents…
          </div>
        )}
        {list.isError && (
          <div className={`flex items-start gap-2 p-4 text-sm ${errorToneClass(list.error)}`}>
            {isPermissionError(list.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>
              {errorText(list.error, 'Failed to load documents.')}
            </span>
          </div>
        )}
        {!isLoading && list.data && blinded && (
          <div className="border-t border-surface-border p-4">
            <BlindedNotice contentLabel="Documents" roleMasterRef={blindedRoleMasterRef} />
          </div>
        )}
        {!isLoading && list.data && !blinded && (
          <div className="overflow-x-auto">
            {/* table-fixed + explicit widths: without this, an unusually
                long file name stretches the File column to fit its full
                text, pushing Status/Version/Uploaded/Uploaded-by (and the
                version-history toggle) off the right edge — the other
                columns are only reachable by scrolling right, which reads
                as "missing" rather than "off-screen". Fixed widths force
                the File column to truncate with an ellipsis instead. */}
            <table className="w-full table-fixed text-sm">
              <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
                <tr>
                  <th className="w-[34%] px-3 py-2">File</th>
                  <th className="w-[14%] px-3 py-2">Status</th>
                  <th className="w-[14%] px-3 py-2">Version</th>
                  <th className="w-[19%] px-3 py-2">Uploaded</th>
                  <th className="w-[19%] px-3 py-2">Uploaded by</th>
                </tr>
              </thead>
              <tbody>
                {isRefreshing ? (
                  <SkeletonRows count={Math.min(Math.max(tabDocs.length, 1), 5)} />
                ) : tabDocs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-sm text-ink-muted">
                      <FileText className="mx-auto mb-2 h-6 w-6 opacity-40" />
                      {activeTab === 'approval_needed'
                        ? 'No documents pending approval.'
                        : activeTab === 'approved'
                          ? 'No approved documents yet.'
                          : canCreateSlot
                            ? 'No documents uploaded yet. Click New document to upload one.'
                            : 'No documents uploaded yet.'}
                    </td>
                  </tr>
                ) : (
                  tabDocs.map((d) => {
                    const v = resolvedVersions[d.document_id] ?? null;
                    const slotLabel = d.title || d.document_type_name || 'Unnamed slot';
                    return (
                      <React.Fragment key={d.document_id}>
                        <tr
                          className={`border-b border-surface-border last:border-0 ${
                            activeTab === 'approval_needed' ? 'last:border-b' : ''
                          }`}
                        >
                          {/* File */}
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 shrink-0 text-ink-muted" />
                              <div className="min-w-0">
                                {v && onOpenViewer ? (
                                  <button
                                    type="button"
                                    className="block w-full truncate text-left font-medium text-accent-700 hover:text-accent-800 hover:underline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onOpenViewer(d.document_id, v.version_id);
                                    }}
                                    title={v.file_name ?? slotLabel}
                                  >
                                    {v.file_name ?? slotLabel}
                                  </button>
                                ) : (
                                  <div
                                    className="block w-full truncate font-medium text-ink-primary"
                                    title={v?.file_name ?? slotLabel}
                                  >
                                    {v?.file_name ?? slotLabel}
                                  </div>
                                )}
                                <div className="truncate text-[10px] text-ink-muted">
                                  {d.document_type_name}
                                </div>
                                {v && (() => {
                                  const tagIds = tagsByDoc[d.document_id] ?? [];
                                  if (tagIds.length === 0) return null;
                                  return (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {tagIds.map((id) => {
                                        const meta = tagLabels.get(id);
                                        if (!meta) return null;
                                        return (
                                          <span
                                            key={id}
                                            className="inline-flex items-center gap-0.5 rounded-full bg-accent-50 px-1.5 py-0.5 text-[10px] text-accent-700"
                                            title={`${meta.category} · ${meta.label}`}
                                          >
                                            <Tag className="h-2.5 w-2.5" />
                                            {meta.label}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </td>
                          {/* Status */}
                          <td className="px-3 py-2 text-xs">
                            <div className="flex items-center gap-1">
                              <span className={`badge ${badgeForBinderStatus(d.status)}`}>
                                {humanStatus(d.status)}
                              </span>
                              {(() => {
                                const exp = expirationBadge(d.expires_at);
                                if (exp.kind === 'expired')
                                  return (
                                    <span className="rounded bg-danger-50 px-1.5 py-0.5 text-[10px] text-danger-700">
                                      Expired {exp.days}d ago
                                    </span>
                                  );
                                if (exp.kind === 'warn')
                                  return (
                                    <span className="rounded bg-warning-50 px-1.5 py-0.5 text-[10px] text-warning-700">
                                      Expires in {exp.days}d
                                    </span>
                                  );
                                return null;
                              })()}
                            </div>
                          </td>
                          {/* Version */}
                          <td className="px-3 py-2 font-mono text-xs text-ink-primary">
                            {v?.version_label ?? '—'}
                          </td>
                          {/* Uploaded */}
                          <td className="px-3 py-2 text-xs text-ink-muted">
                            {v ? fmtDateTime(v.uploaded_at) : '—'}
                          </td>
                          {/* Uploaded by */}
                          <td className="px-3 py-2 text-xs text-ink-muted">
                            {v
                              ? (resolvedUploaderNames[v.uploaded_by] ||
                                  siteUserNameMap.get(v.uploaded_by) ||
                                  '—')
                              : '—'}
                          </td>
                        </tr>

                        {/* Older versions — All tab only. Approval Needed / Approved are
                            filtered, purpose-built views (routing status / approval outcome);
                            version history is noise there. */}
                        {activeTab === 'all' && v && v.version_number > 1 && (
                          <VersionHistoryRows
                            binderId={binderId}
                            documentId={d.document_id}
                            currentVersionId={v.version_id}
                            siteUserNameMap={siteUserNameMap}
                          />
                        )}

                        {/* Approval routing rows — only in Approval Needed tab */}
                        {activeTab === 'approval_needed' && v && (
                          <ApprovalRoutingPanel
                            binderId={binderId}
                            docId={d.document_id}
                            versionId={v.version_id}
                          />
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NewDocumentSlotDialog
        binderId={binderId}
        folderId={folderId}
        folderLabel={folderLabel}
        folderLevel={folderLevel}
        canUploadVersion={canUploadVersion}
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(doc) => {
          qc.invalidateQueries({ queryKey });
          // The folder list's fresh `current_versions` entry for this doc can
          // get clobbered by a stale `fallbackBatchQ` cache hit (same
          // missingDocIds key, 30s staleTime) if an earlier upload's
          // uploader-name lookup was still resolving — resolvedVersions
          // spreads the fallback data last, so it silently wins. Invalidate
          // both caches so the row reflects the version that was just
          // uploaded instead of whatever the fallback still has cached.
          qc.invalidateQueries({ queryKey: ['binder-versions-batch', binderId, folderId] });
          qc.invalidateQueries({ queryKey: ['binder-versions', binderId, doc.document_id] });
        }}
      />
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Placeholder rows shown while the folder's document list is (re)loading. */
function SkeletonRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <tr key={i} className="border-b border-surface-border last:border-0 animate-pulse">
          <td className="px-3 py-2">
            <div className="h-4 w-40 rounded bg-surface-muted" />
            <div className="mt-1.5 h-2.5 w-24 rounded bg-surface-muted" />
          </td>
          <td className="px-3 py-2"><div className="h-4 w-16 rounded-full bg-surface-muted" /></td>
          <td className="px-3 py-2"><div className="h-4 w-10 rounded bg-surface-muted" /></td>
          <td className="px-3 py-2"><div className="h-4 w-24 rounded bg-surface-muted" /></td>
          <td className="px-3 py-2"><div className="h-4 w-20 rounded bg-surface-muted" /></td>
        </tr>
      ))}
    </>
  );
}

function badgeForBinderStatus(s: BinderStatus): string {
  switch (s) {
    case 'draft':       return 'badge-neutral';
    case 'preview':     return 'badge-info';
    case 'in_approval': return 'badge-warning';
    case 'current':     return 'badge-success';
    case 'superseded':  return 'badge-purple';
    case 'archived':    return 'badge-neutral';
    default:            return 'badge-neutral';
  }
}

function routingStatusBadge(status: ApprovalRoutingApprover['status']): string {
  switch (status) {
    case 'signed':   return 'badge-success';
    case 'rejected': return 'badge-danger';
    case 'pending':  return 'badge-warning';
    case 'waiting':  return 'badge-neutral';
    case 'skipped':  return 'badge-info';
    default:         return 'badge-neutral';
  }
}

function expirationBadge(expiresAt: string | null): { kind: 'expired' | 'warn' | null; days: number } {
  if (!expiresAt) return { kind: null, days: 0 };
  const exp = new Date(expiresAt).getTime();
  const now = Date.now();
  const diffDays = Math.floor((exp - now) / 86400000);
  if (diffDays < 0) return { kind: 'expired', days: -diffDays };
  if (diffDays <= 30) return { kind: 'warn', days: diffDays };
  return { kind: null, days: 0 };
}
